import { isDifferential, isNumberString, isPowerOperator } from "@corca/core";

import { fn, numberExpr, op, SympyBuildError } from "./shared";

import type { SympyBuilder } from "./SympyBuilder";
import type { SemanticExpr } from "../types";
import type { NodeJson, NodeJsonMap } from "@corca/data";

export function parseDerivative(
  builder: SympyBuilder,
  node: NodeJsonMap["operator"],
  order: number,
): SemanticExpr {
  const derivativeChildren = node.children;
  const functionNode = derivativeChildren[0];

  const funcExpr = builder.traverseNode(functionNode, 2);
  if (funcExpr === null) {
    throw new SympyBuildError(`Empty function in derivative`);
  }

  if (builder.variables.size === 0) {
    throw new SympyBuildError(`No variables found for derivative`);
  }

  const fallbackVar: SemanticExpr = {
    type: "symbol",
    name: builder.variables.keys().next().value!,
  };
  const diffVar = fn("sympy_json.diff_var", [funcExpr, fallbackVar]);

  return op("Derivative", [funcExpr, [diffVar, numberExpr(String(order))]]);
}

export type DenominatorPair = { diffFunc: NodeJson; externalPower: NodeJson | null };

export function pairDenominatorDifferentials(children: NodeJson[]): DenominatorPair[] | null {
  const pairs: DenominatorPair[] = [];
  let i = 0;
  while (i < children.length) {
    const current = children[i];
    if (!isDifferential(current)) return null;

    const next = children[i + 1];
    const hasExternalPower = isPowerOperator(next);

    pairs.push({ diffFunc: current, externalPower: hasExternalPower ? next : null });
    i += hasExternalPower ? 2 : 1;
  }
  return pairs.length > 0 ? pairs : null;
}

function extractDifferentialOrderAndBody(
  diffFunc: NodeJson,
): { orderNode: NodeJson | null; bodyInline: NodeJson } | null {
  const inlines = diffFunc.children.filter((c) => c.type === "inline");
  if (inlines.length === 0) return null;
  const bodyInline = inlines[inlines.length - 1];

  const orderNode = diffFunc.children.find(isPowerOperator) ?? null;

  return { orderNode, bodyInline };
}

export function parseFractionDerivative(
  builder: SympyBuilder,
  numeratorFunc: NodeJson,
  denominatorPairs: DenominatorPair[],
): SemanticExpr | null {
  const numParts = extractDifferentialOrderAndBody(numeratorFunc);
  if (!numParts) return null;

  const exprSem = builder.traverseNode(numParts.bodyInline, 2);
  if (exprSem === null) return null;

  const numeratorOrder = numParts.orderNode
    ? builder.traverseNode(numParts.orderNode.children[0], 2)
    : null;
  const numeratorOrderExpr = numeratorOrder === null ? null : builder.emitInline(numeratorOrder);

  // Each variable-group is a bare `[base, order]` array (or a bare base for order 1),
  // matching how SymPy serializes Derivative's Tuple limits.
  const varGroups: (SemanticExpr | SemanticExpr[])[] = [];
  let totalOrder = 0;
  let hasSymbolicOrder = numeratorOrderExpr !== null && !isNumberString(numeratorOrderExpr);

  for (const { diffFunc, externalPower } of denominatorPairs) {
    const dParts = extractDifferentialOrderAndBody(diffFunc);
    if (!dParts) return null;

    const dInline = dParts.bodyInline;
    const { children } = dInline;
    if (children.length === 0) return null;

    let baseSem: SemanticExpr | null = null;
    let orderSem: SemanticExpr | null = null;

    if (externalPower) {
      baseSem = builder.traverseNode(dInline, 2);
      orderSem = builder.traverseNode(externalPower.children[0], 2);
    } else {
      const lastChild = children[children.length - 1];
      const internalPower = isPowerOperator(lastChild) ? lastChild : null;

      if (internalPower) {
        const baseNodes = children.slice(0, -1);
        if (baseNodes.length === 0) return null;
        baseSem = builder.traverseNode({ ...dInline, children: baseNodes }, 2);
        orderSem = builder.traverseNode(internalPower.children[0], 2);
      } else {
        baseSem = builder.traverseNode(dInline, 2);
      }
    }

    if (baseSem === null) return null;

    if (orderSem) {
      varGroups.push([baseSem, orderSem]);
      const orderStr = builder.emitInline(orderSem);
      if (isNumberString(orderStr)) {
        totalOrder += Number(orderStr);
      } else {
        hasSymbolicOrder = true;
      }
    } else {
      varGroups.push(baseSem);
      totalOrder += 1;
    }
  }

  if (varGroups.length === 0) return null;

  if (numeratorOrderExpr && !hasSymbolicOrder && isNumberString(numeratorOrderExpr)) {
    if (Number(numeratorOrderExpr) !== totalOrder) return null;
  }

  return op("Derivative", [exprSem, ...varGroups]);
}
